import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import type { PayableBill } from "../payable-bill.entity";

const money = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

type Props = {
  open: boolean;
  bill: PayableBill | null;
  cashSessionOpen: boolean;
  onClose: () => void;
  onConfirm: (registerWithdrawal: boolean) => void;
};

export function MarkPaidDialog({ open, bill, cashSessionOpen, onClose, onConfirm }: Props) {
  const theme = useTheme();
  const [registerWithdrawal, setRegisterWithdrawal] = useState(true);

  useEffect(() => {
    if (open) setRegisterWithdrawal(true);
  }, [open]);

  if (!bill) return null;

  const recurring = bill.getIsRecurring() && bill.getRecurrence() !== "none";
  const recurrenceLabel =
    bill.getRecurrence() === "weekly" ? "semanal" :
    bill.getRecurrence() === "monthly" ? "mensal" :
    bill.getRecurrence() === "yearly" ? "anual" : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, fontWeight: 800, fontSize: "1.1rem" }}>
        {recurring ? "Registrar pagamento recorrente" : "Confirmar pagamento"}
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2.5}>
          {/* ── Info da conta ── */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" fontWeight={700}>
                {bill.getTitle()}
              </Typography>
              {bill.getPayeeName() && (
                <Typography variant="body2" color="text.secondary">
                  {bill.getPayeeName()}
                </Typography>
              )}
              <Typography variant="h6" fontWeight={800} color="primary.dark" sx={{ mt: 0.5 }}>
                {money(bill.getAmount())}
              </Typography>
              {recurring && (
                <Typography variant="caption" color="text.secondary">
                  Recorrência {recurrenceLabel} · próximo vencimento será avançado automaticamente
                </Typography>
              )}
            </Stack>
          </Box>

          {/* ── Seção de caixa ── */}
          {cashSessionOpen ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.warning.main, 0.3),
                bgcolor: alpha(theme.palette.warning.main, 0.04),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <PointOfSaleOutlinedIcon sx={{ color: "warning.main", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Caixa aberto
                </Typography>
              </Stack>

              <FormControlLabel
                control={
                  <Switch
                    checked={registerWithdrawal}
                    onChange={(e) => setRegisterWithdrawal(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label={
                  <Stack>
                    <Typography variant="body2" fontWeight={600}>
                      Registrar retirada no caixa
                    </Typography>
                    {registerWithdrawal && (
                      <Typography variant="caption" color="text.secondary">
                        Categoria <strong>Pagar conta</strong> · {money(bill.getAmount())} · vinculado a esta conta
                      </Typography>
                    )}
                  </Stack>
                }
                sx={{ ml: 0, alignItems: "flex-start" }}
              />
            </Box>
          ) : (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              Nenhum caixa aberto. O pagamento será registrado apenas nas contas a pagar.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ fontWeight: 700 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={() => onConfirm(cashSessionOpen && registerWithdrawal)}
          sx={{ fontWeight: 700 }}
        >
          Confirmar pagamento
        </Button>
      </DialogActions>
    </Dialog>
  );
}
